/**
 * TaskDrawerDetails Component
 *
 * Renders the details/metadata card in the drawer sidebar including:
 * - Urgency with color indicator
 * - Start date and due date
 * - Created date and requestor
 * - Channel partner and advertiser
 * - Request type and subtype
 * - External link (if available)
 *
 * Accessibility:
 * - Semantic definition list for metadata
 * - ARIA labels for dates and status indicators
 * - Color-blind friendly urgency indicators (includes text label)
 *
 * @see WCAG 2.1 AA - 1.4.1 Use of Color
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Card, Tooltip } from 'antd';
import {
  LinkOutlined,
  SyncOutlined,
  RightOutlined,
} from '@ant-design/icons';
import Text from 'antd/lib/typography/Text';
import { startCase } from 'lodash';
import { urgencyHexColors } from '../../configs/task.config';
import '../../styles/index.scss';

/**
 * Format a date string for display
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date or '-'
 */
const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

/**
 * Get relative time string
 * @param {string} dateString - ISO date string
 * @returns {string} Relative time or empty
 */
const getRelativeTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'today';
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return '';
};

const TaskDrawerDetails = ({
  currentTask,
}) => {
  const handleLinkClick = () => {
    const url = currentTask.linkUrl;
    if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const createdRelative = getRelativeTime(currentTask.createdAt);

  return (
    <Card
      size="small"
      title={
        <div className="task-drawer-full__card-header">
          <span id="details-heading" className="task-drawer-full__card-title">
            Details
          </span>
          {currentTask.linkUrl && (
            <Tooltip title="Open external link">
              <LinkOutlined
                className="task-drawer-full__edit-icon"
                onClick={handleLinkClick}
                role="button"
                aria-label="Open external link in new tab"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleLinkClick();
                  }
                }}
              />
            </Tooltip>
          )}
        </div>
      }
      className="task-drawer-full__card"
      role="region"
      aria-labelledby="details-heading"
    >
      {/* Grid Layout for Details */}
      <div className="task-drawer-full__details-grid" role="list" aria-label="Task details">
        {/* Urgency */}
        <div className="task-drawer-full__details-item" role="listitem">
          <Text
            type="secondary"
            className="task-drawer-full__detail-label"
            id="urgency-label"
          >
            Urgency
          </Text>
          {currentTask.urgency ? (
            <span
              className={`task-drawer-full__urgency-chip${currentTask.urgency === 'CRITICAL' ? ' task-drawer-full__urgency-chip--critical' : ''}`}
              style={{ '--badge-color': urgencyHexColors[currentTask.urgency] }}
              aria-labelledby="urgency-label"
            >
              <span
                className="task-drawer-full__status-dot"
                aria-hidden="true"
              />
              {startCase(currentTask.urgency.toLowerCase())}
            </span>
          ) : (
            <Text className="task-drawer-full__detail-value--muted">-</Text>
          )}
        </div>

        {/* Due Date */}
        <div className="task-drawer-full__details-item" role="listitem">
          <Text type="secondary" className="task-drawer-full__detail-label">
            Due Date
          </Text>
          <Text
            className="task-drawer-full__detail-value"
            aria-label={`Due date: ${formatDate(currentTask.dueDate)}${currentTask.recurring ? ', recurring task' : ''}`}
          >
            {formatDate(currentTask.dueDate)}
            {currentTask.recurring && (
              <Tooltip title="Recurring task">
                <SyncOutlined
                  className="task-drawer-full__icon-suffix"
                  aria-label="Recurring task"
                />
              </Tooltip>
            )}
          </Text>
        </div>

        {/* Start Date */}
        <div className="task-drawer-full__details-item" role="listitem">
          <Text type="secondary" className="task-drawer-full__detail-label">
            Start Date
          </Text>
          <Text
            className="task-drawer-full__detail-value"
            aria-label={`Start date: ${formatDate(currentTask.startDate)}`}
          >
            {formatDate(currentTask.startDate)}
          </Text>
        </div>

        {/* Created On */}
        <div className="task-drawer-full__details-item" role="listitem">
          <Text type="secondary" className="task-drawer-full__detail-label">
            Created
          </Text>
          <Text className="task-drawer-full__detail-value">
            {formatDate(currentTask.createdAt)}
            {createdRelative && (
              <span className="task-drawer-full__detail-relative">
                ({createdRelative})
              </span>
            )}
          </Text>
        </div>

        {/* Divider */}
        <div className="task-drawer-full__details-divider" aria-hidden="true" />

        {/* Requested By */}
        <div className="task-drawer-full__details-item task-drawer-full__details-item--full-width" role="listitem">
          <Text type="secondary" className="task-drawer-full__detail-label">
            Requested By
          </Text>
          <Text className="task-drawer-full__detail-value">
            {startCase(
              currentTask.createdBy?.name ||
              currentTask.createdBy?.username ||
              'Unknown'
            )}
          </Text>
        </div>

        {/* Request Type with Subtype */}
        <div className="task-drawer-full__details-item task-drawer-full__details-item--full-width" role="listitem">
          <Text type="secondary" className="task-drawer-full__detail-label">
            Request Type
          </Text>
          {currentTask.requestType ? (
            <div className="task-drawer-full__request-type">
              <span>{currentTask.requestType}</span>
              {currentTask.requestSubType && (
                <>
                  <RightOutlined className="task-drawer-full__request-arrow" />
                  <span className="task-drawer-full__request-subtype">
                    {currentTask.requestSubType}
                  </span>
                </>
              )}
            </div>
          ) : (
            <Text className="task-drawer-full__detail-value--muted">-</Text>
          )}
        </div>

        {/* Channel Partner (conditional) - full width row */}
        {currentTask.channelPartner && (
          <div className="task-drawer-full__details-item task-drawer-full__details-item--full-width" role="listitem">
            <Text type="secondary" className="task-drawer-full__detail-label">
              Channel Partner
            </Text>
            <Text className="task-drawer-full__detail-value">
              {currentTask.channelPartner.name}
            </Text>
          </div>
        )}

        {/* Advertiser (conditional) - full width row */}
        {currentTask.advertiser && (
          <div className="task-drawer-full__details-item task-drawer-full__details-item--full-width" role="listitem">
            <Text type="secondary" className="task-drawer-full__detail-label">
              Advertiser
            </Text>
            <Text className="task-drawer-full__detail-value">
              {currentTask.advertiser.name}
            </Text>
          </div>
        )}
      </div>
    </Card>
  );
};

TaskDrawerDetails.propTypes = {
  currentTask: PropTypes.shape({
    id: PropTypes.string,
    urgency: PropTypes.oneOf(['LOW', 'MEDIUM', 'HIGH', 'CRITICAL']),
    startDate: PropTypes.string,
    dueDate: PropTypes.string,
    createdAt: PropTypes.string,
    recurring: PropTypes.bool,
    linkUrl: PropTypes.string,
    requestType: PropTypes.string,
    requestSubType: PropTypes.string,
    createdBy: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      username: PropTypes.string,
    }),
    channelPartner: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
    advertiser: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
  }).isRequired,
};

export default TaskDrawerDetails;
